define((require) => {
    "use strict";

    var CreateWidgetHelper = require("ui/editor/CreateWidgetHelper"),
        WidgetTypes = require("shared/ui/experience/widgets/WidgetTypes"),
        Promise = require("shared/util/Promise"),
        UUIDGenerator = require("ui/experience/widgets/UUIDGenerator"),
        LoggedInUserState = require("shared/ui/user/LoggedInUserState"),
        CurrencyMapper = require("shared/ui/experience/widgets/dynamicProductWidget/CurrencyMapper"),
        EditorEventFilters = require("ui/editor/EditorEventFilters").default,
        MathUtil = require("shared/util/MathUtil").default,
        DynamicProductWidgetModel = require("shared/ui/experience/widgets/dynamicProductWidget/DynamicProductWidgetModel"),
        DynamicProductWidgetSubtypes = require("shared/ui/experience/widgets/dynamicProductWidget/DynamicProductWidgetSubtypes").default;

    return {

        _imageProperties: {
            width: 250, height: 175, xOffset: 0, yOffset: 0, isDynamic: false
        },

        _priceProperties: {
            width: 150, xOffset: 0, yOffset: 180, fontSize: 24, isDynamic: true
        },

        _titleProperties: {
            width: 250, xOffset: 0, yOffset: 215, fontSize: 18, isDynamic: true
        },

        _descriptionProperties: {
            width: 250, xOffset: 0, yOffset: 265, fontSize: 14, isDynamic: true
        },

        createProductWidget(product, experience, dynamicProductWidget) {
            const keys = Object.keys(product.get("products"));
            if (keys.length > 0) {
                // We assume that the answer file either has 0 or 1 product
                const correctProductID = keys[0];
                let currentProduct = product.get("products")[keys[0]];
                if (currentProduct) {
                    return this._createProductWidgetTemplate({
                        experience: experience,
                        dynamicProductWidget: dynamicProductWidget,
                        productID: correctProductID,
                        title: currentProduct.title,
                        description: currentProduct.description,
                        price: `${this._getCurrency(experience.get("language"))}${currentProduct.priceValue}`
                    });
                }
            }
        },

        updateProductWidget(product, experience, dynamicProductWidget) {
            const keys = Object.keys(product.get("products"));
            if (keys.length > 0) {
                // We assume that the answer file either has 0 or 1 product
                let currentProduct = product.get("products")[keys[0]];
                if (currentProduct) {
                    return this._updateProductWidgetTemplate(
                        dynamicProductWidget, currentProduct.id,
                        {
                            title: currentProduct.title,
                            description: currentProduct.description,
                            price: `${this._getCurrency(experience.get("language"))}${currentProduct.priceValue}`
                        }
                    );
                }
            }
        },

        /**
         * Updates a productWidget in the scene when a product ID is specified
         *
         * Generates a DynamicProductWidget widget and moves the specified widgets from the scene widget collection to the dynamic product widget.
         * The newly generated widget is then added to the scene.
         *
         * @param  {DynamicProductWidgetModel} previousProductWidgetModel The previous empty DynamicProductWidget.
         *  (one without a productID the workflow for a widget with a productID is different)
         * @param  {String} productID The productID of the new DynamicProductWidgetModel
         * @param  {widget} widgets The widgets to be added to the DynamicProductWidgetModel
         * @return {DynamicProductWidgetModel} newProductWidget The newly created and updated DynamicProductWidgetModel
         */
        updateProductWidgetOnScene(scene, previousProductWidgetModel, productID, widgets) {
            const widgetsOfGroup = widgets.map((widget) => widget.clone());

            // Sort the widgets to make sure layerIndex is respected
            widgetsOfGroup.sort((a, b) => {
                return a.get("layerIndex") - b.get("layerIndex");
            });

            // Deletes the old widgets
            scene.deleteWidgets(widgets, {cause: EditorEventFilters.GROUP});

            // We create and delete the product widget because it's the easiest way to redraw the widget

            const uuid = previousProductWidgetModel.get("uuid");

            scene.deleteWidgetByID(previousProductWidgetModel.get("uuid"), {cause: EditorEventFilters.GROUP});

            const newProductWidget = new DynamicProductWidgetModel({"group": widgetsOfGroup});
            newProductWidget.set("productID", productID, {cause: EditorEventFilters.SUPERFICIAL});
            newProductWidget.set("inputProductID", productID, {cause: EditorEventFilters.SUPERFICIAL});
            newProductWidget.set("uuid", uuid, {cause: EditorEventFilters.SUPERFICIAL});

            const lowestWidgetIndex = scene.getLowestIndex(widgetsOfGroup);

            scene.addWidget(newProductWidget, {
                cause: EditorEventFilters.SUPERFICIAL,
                at: lowestWidgetIndex - 1
            });

            scene.trigger("selectWidgets", [newProductWidget]);

            scene.reindexLayers(scene.get("widgets").models, 0, {cause: EditorEventFilters.SUPERFICIAL});

            return newProductWidget;
        },

        replaceProductWidget(scene, currentProductWidgetModel, nextProductWidgetModel) {
            scene.deleteWidgetByID(currentProductWidgetModel.get("uuid"), {cause: EditorEventFilters.UNDO});

            scene.addWidget(nextProductWidgetModel, {
                cause: EditorEventFilters.UNDO
            });

            scene.reindexLayers(scene.get("widgets").models, 0, {cause: EditorEventFilters.SUPERFICIAL});
        },

        _getCurrency(language) {
            return CurrencyMapper.getCurrencyMapping(language);
        },

        _updateProductWidgetTemplate(dynamicProductWidget, newProductID, options) {
            let widgets = dynamicProductWidget.get("group");

            dynamicProductWidget.set("inputProductID", newProductID, {cause: EditorEventFilters.SUPERFICIAL});
            dynamicProductWidget.set("productID", newProductID, {cause: EditorEventFilters.SUPERFICIAL});

            const {
                description: descriptionField,
                price: priceField,
                title: titleField
            } = options;

            this._updateDynamicProductText(descriptionField, DynamicProductWidgetSubtypes.Description, widgets);
            this._updateDynamicProductText(priceField, DynamicProductWidgetSubtypes.Price, widgets);
            this._updateDynamicProductText(titleField, DynamicProductWidgetSubtypes.Title, widgets);

            return dynamicProductWidget;
        },

        _updateDynamicProductText(text, subtype, widgetList) {
            widgetList.forEach((widget) => {
                if (widget.get("subtype") === subtype) {
                    widget.set("text", text.toString(), {cause: EditorEventFilters.SUPERFICIAL});
                }
            });
        },

        _createProductWidgetTemplate(options) {
            // We create the widget UUID's here because we need them at this point and they don't exist yet
            const imageWidgetUUID = UUIDGenerator.createUUID();
            const priceWidgetUUID = UUIDGenerator.createUUID();
            const titleWidgetUUID = UUIDGenerator.createUUID();
            const descriptionWidgetUUID = UUIDGenerator.createUUID();

            return this._createProductWidgetHelper({
                "productWidget": options.dynamicProductWidget,
                "experience": options.experience,
                "title": options.title,
                "description": options.description,
                "price": options.price,
                "imageWidgetUUID": imageWidgetUUID,
                "priceWidgetUUID": priceWidgetUUID,
                "titleWidgetUUID": titleWidgetUUID,
                "descriptionWidgetUUID": descriptionWidgetUUID
            });
        },

        _createProductWidgetHelper(options) {
            const productX = MathUtil.invertScaleByExperienceSize(options.productWidget.get("left"), options.experience.get("width"));
            const productY = MathUtil.invertScaleByExperienceSize(options.productWidget.get("top"), options.experience.get("height"));
            let textWidgetType;
            if (LoggedInUserState.get("companySettings").get("enhancedTextWidgetEnabled")) {
                // Use ETW when ETW is enabled
                textWidgetType = WidgetTypes.TEXT_ENHANCED;
            } else {
                textWidgetType = WidgetTypes.TEXT;
            }

            // "unscaleWidgetWith" and "unscaledWidgetHeight" is set because width and height are constantly
            // being modified and changed and we need to know the fixed width and height of the image widget
            // without it being scaled
            const imageWidget = this._generateProductImageSubwidget({
                "experience": options.experience,
                "subtype": DynamicProductWidgetSubtypes.Image,
                "type": WidgetTypes.IMAGE,
                "uuid": options.imageWidgetUUID,
                "isDynamic": this._imageProperties.isDynamic,
                "width": this._imageProperties.width,
                "height": this._imageProperties.height,
                "x": productX + this._imageProperties.xOffset,
                "y": productY + this._imageProperties.yOffset,
                "scaleOnInitialize": true,
                "unscaledWidgetWidth": this._imageProperties.width,
                "unscaledWidgetHeight": this._imageProperties.height
            });

            const priceWidget = this._generateProductTextSubwidget({
                "experience": options.experience,
                "subtype": DynamicProductWidgetSubtypes.Price,
                "type": textWidgetType,
                "uuid": options.priceWidgetUUID,
                "isDynamic": this._priceProperties.isDynamic,
                "width": this._priceProperties.width,
                "height": null,
                "x": productX + this._priceProperties.xOffset,
                "y": productY + this._priceProperties.yOffset,
                "text": options.price.toString(),
                "fontSize": this._priceProperties.fontSize
            });

            const titleWidget = this._generateProductTextSubwidget({
                "experience": options.experience,
                "subtype": DynamicProductWidgetSubtypes.Title,
                "type": textWidgetType,
                "uuid": options.titleWidgetUUID,
                "isDynamic": this._titleProperties.isDynamic,
                "width": this._titleProperties.width,
                "height": null,
                "x": productX + this._titleProperties.xOffset,
                "y": productY + this._titleProperties.yOffset,
                "text": options.title,
                "fontSize": this._titleProperties.fontSize
            });

            const descriptionWidget = this._generateProductTextSubwidget({
                "experience": options.experience,
                "subtype": DynamicProductWidgetSubtypes.Description,
                "type": textWidgetType,
                "uuid": options.descriptionWidgetUUID,
                "isDynamic": this._descriptionProperties.isDynamic,
                "width": this._descriptionProperties.width,
                "height": null,
                "x": productX + this._descriptionProperties.xOffset,
                "y": productY + this._descriptionProperties.yOffset,
                "text": options.description,
                "fontSize": this._descriptionProperties.fontSize
            });

            return Promise.resolved([imageWidget, titleWidget, priceWidget, descriptionWidget]);
        },

        _generateProductImageSubwidget(options) {
            const widget = this._createAndSetProductWidgetProperties(options);

            widget.set(
                "scaleOnInitialize", options.scaleOnInitialize,
                {cause: EditorEventFilters.SUPERFICIAL}
            );
            widget.set(
                "unscaledWidgetWidth", options.unscaledWidgetWidth,
                {cause: EditorEventFilters.SUPERFICIAL}
            );
            widget.set(
                "unscaledWidgetHeight", options.unscaledWidgetHeight,
                {cause: EditorEventFilters.SUPERFICIAL}
            );

            return widget;
        },

        _generateProductTextSubwidget(options) {
            const experience = options.experience;
            const experienceSize = {height: experience.height, width: experience.width};

            const widget = this._createAndSetProductWidgetProperties(options);

            widget.set("text", options.text, experienceSize);
            widget.set("fontSize", options.fontSize, {cause: EditorEventFilters.SUPERFICIAL});

            return widget;
        },

        _createAndSetProductWidgetProperties(options) {
            const experience = options.experience;
            const experienceSize = {height: experience.get("height"), width: experience.get("width")};

            const point = {x: options.x, y: options.y};

            const widget = this._createWidget(options, experienceSize, point);

            // set the left and top because we do not want undefined behavior
            this._setCoordinate(widget, point, experienceSize);

            // Apparently they scale by experience size
            this._setSize(widget, experience, options.width, options.height);
            return widget;
        },

        _createWidget(options, size, point) {
            const widgetOptions = CreateWidgetHelper.WIDGET_OPTIONS[options.type];
            const extendOptions = CreateWidgetHelper.makeExtendOptions(size, point);
            const extendedOptions = extendOptions(widgetOptions);
            extendedOptions.uuid = options.uuid;
            extendedOptions.subtype = options.subtype;
            extendedOptions.isDynamic = options.isDynamic;
            const widget = CreateWidgetHelper.createWidget(size, options.type, extendedOptions);

            return widget;
        },

        _setCoordinate(widget, coordinate, experienceSize) {
            const left = MathUtil.scaleByExperienceSize(coordinate.x, experienceSize.width);
            const top = MathUtil.scaleByExperienceSize(coordinate.y, experienceSize.height);
            widget.set("left", left, {cause: EditorEventFilters.SUPERFICIAL});
            widget.set("top", top, {cause: EditorEventFilters.SUPERFICIAL});
        },

        _setSize(widget, experience, width, height) {
            if (width) {
                const scaledWidth = MathUtil.scaleByExperienceSize(width, experience.get("width"));
                widget.set("width", scaledWidth, {cause: EditorEventFilters.SUPERFICIAL});
                widget.set("scaledWidgetWidth", scaledWidth, {cause: EditorEventFilters.SUPERFICIAL});
            }

            if (height) {
                const scaledHeight = MathUtil.scaleByExperienceSize(height, experience.get("height"));
                widget.set("height", scaledHeight, {cause: EditorEventFilters.SUPERFICIAL});
                widget.set("scaledWidgetHeight", scaledHeight, {cause: EditorEventFilters.SUPERFICIAL});
            }
        }
    };
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/dynamicProductWidget/CreateAndUpdateDynamicProductWidget.js